import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { useMissionRoles, useAuth, useLinks } from "hooks";
import { DIRECTION, MISSION_ROLE_STATES } from "constants/index";
import {
  PublicViewSettingsForm,
  ReorganizeRoleForm,
  RoleCard,
  RoleForm,
  RolePublishModal,
} from "components/Missions/Roles";
import { useInView } from "react-intersection-observer";
import { DataTableProvider } from "components/DataTable";
import Card, { CardList } from "components/Card";
import { PAGINATION_TYPE } from "components/Pagination";
// Fixme: The named import from index is giving errors
import { default as MISSION_ROLES_COLUMNS } from "constants/missionRolesColumns";

/**
 * RolesList
 *
 * @param {Boolean}   isCardView
 * @param {String}    missionId
 * @param {Object}    filters
 * @param {Number}    resultsPerPage
 * @param {Array}     columns
 * @param {Boolean}   selectable
 * @param {Function}  onSelect
 * @param {Array}     selectionInitialState
 * @param {Boolean}   showAdvancedOptions
 */
const RolesList = ({
  isCardView,
  missionId,
  filters,
  resultsPerPage,
  columns,
  selectable,
  onSelect,
  selectionInitialState,
  showAdvancedOptions,
  ...props
}) => {
  const navigate = useNavigate();
  const links = useLinks();
  const paginationType = isCardView ? PAGINATION_TYPE.append : PAGINATION_TYPE.classic;
  const { ref, inView } = useInView();
  const { isClient, isAdmin, isTalentSimpleClient } = useAuth();

  const {
    loading,
    loadingMore,
    data,
    error,
    hasNextPage,
    handleNextPage,
    hasPreviousPage,
    handlePreviousPage,
    showForm,
    setShowForm,
    showReorganizeForm,
    setShowReorganizeForm,
    showPublicViewForm,
    setShowPublicViewForm,
    showPublishModal,
    setShowPublishModal,
    publishRole,
    shouldNotifySubscribers,
    setShouldNotifySubscribers,
    handleConfirmPublishRole,
    publishing,
    selectedRole,
    getAdvancedOptions,
    currentPage,
    totalCount,
    pageInfo,
    handlePageChange,
  } = useMissionRoles({
    ...props,
    missionId,
    resultsPerPage,
    paginationType,
    filters,
    orderBy: { state: DIRECTION.ascending },
    includeCustomerBudget: isAdmin || isTalentSimpleClient,
  });

  useEffect(() => {
    if (isCardView && inView && hasNextPage && !loading && !loadingMore) {
      handleNextPage();
    }
  }, [inView, hasNextPage, loading, loadingMore, isCardView]);

  const isRolePublished = ({ state }) => state === MISSION_ROLE_STATES.published;

  const contextfulColumns = useMemo(() => {
    const context = { isClient, navigate, links };
    const buildColumnsContext = (cols, context) =>
      cols?.filter(Boolean)?.map((col) => (typeof col === "function" ? col(context) : col));
    return buildColumnsContext(columns ?? ALL_COLUMNS, context);
  }, [columns, isClient]);

  return (
    <>
      {isCardView && (
        <CardList
          loading={loading || loadingMore}
          data={data}
          resultsPerPage={resultsPerPage}
          cols={2}
          renderer={RoleCard}
          {...props}
        />
      )}
      {!isCardView && (
        <Card>
          <DataTableProvider
            data={data}
            error={error}
            loading={loading || loadingMore}
            resultsPerPage={resultsPerPage}
            columns={contextfulColumns}
            onSelect={onSelect}
            selectionInitialState={selectionInitialState}
            selectable={selectable}
            rowOptions={showAdvancedOptions ? (data) => getAdvancedOptions(data) : null}
            headerOptions={showAdvancedOptions ? (selection) => getAdvancedOptions(null, selection) : null}
            selectFieldDisabledFunc={showAdvancedOptions ? isRolePublished : undefined}
            headerOptionsTooltipIdFunc={({ data, selectableDataIds }) =>
              data?.length > 0 && selectableDataIds?.length === 0 && "Missions.Roles.RolesList.AllRolesPublishedTooltip"
            }
            paginationType={paginationType}
            paginationProps={{
              type: paginationType,
              resultsPerPage,
              onNextPage: handleNextPage,
              onPreviousPage: handlePreviousPage,
              hasNextPage: !loading && hasNextPage,
              hasPreviousPage: !loading && hasPreviousPage,
              loading,
              currentPage,
              pageInfo,
              handlePageChange,
              totalCount,
            }}
            {...props}
          />
        </Card>
      )}
      <div ref={ref} />
      <RoleForm
        show={showForm}
        onClose={() => setShowForm(false)}
        missionId={selectedRole?.mission?.uuid}
        isEditing={true}
        data={selectedRole}
      />
      <ReorganizeRoleForm show={showReorganizeForm} onClose={() => setShowReorganizeForm(false)} role={selectedRole} />
      <PublicViewSettingsForm
        show={showPublicViewForm}
        onClose={() => setShowPublicViewForm(false)}
        data={selectedRole}
      />
      <RolePublishModal
        show={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onSubmit={handleConfirmPublishRole}
        role={publishRole}
        shouldNotifySubscribers={shouldNotifySubscribers}
        setShouldNotifySubscribers={setShouldNotifySubscribers}
        loading={publishing}
      />
    </>
  );
};

const ROLE_AND_SKILLS_COLUMNS = [MISSION_ROLES_COLUMNS.role, MISSION_ROLES_COLUMNS.skillsAndDisciplines];

const COLUMN_DATA = [
  MISSION_ROLES_COLUMNS.organization,
  MISSION_ROLES_COLUMNS.talentOwner,
  MISSION_ROLES_COLUMNS.targetRate,
  MISSION_ROLES_COLUMNS.hours,
  MISSION_ROLES_COLUMNS.status,
];

const ALL_COLUMNS = [...ROLE_AND_SKILLS_COLUMNS, ...COLUMN_DATA];

RolesList.defaultProps = {
  resultsPerPage: 20,
  selectable: true,
  filters: {},
  showAdvancedOptions: true,
};

RolesList.propTypes = {
  isCardView: PropTypes.bool,
  missionId: PropTypes.string,
  filters: PropTypes.object,
  columns: PropTypes.array,
  resultsPerPage: PropTypes.number,
  selectable: PropTypes.bool,
  onSelect: PropTypes.func,
  selectionInitialState: PropTypes.array,
  showAdvancedOptions: PropTypes.bool,
};

export { COLUMN_DATA, ROLE_AND_SKILLS_COLUMNS };

export default RolesList;
